import React from "react";
import {
  Box,
  Typography,
  Alert,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PerLineWeightRow from "./PerLineWeightRow";
import { usePerLineWeights } from "@/hooks/use-per-line-weights";
import HPButton from "../common/Button";

interface PerLineWeightConfigProps {
  datasetId: string;
  lineLabels: string[]; // Array of line labels from chart (series.name)
  lineIds: string[]; // Array of line IDs from chart (series.id)
  customLineLabels?: Record<string, string>; // Custom renamed labels for lines
  hiddenLines?: string[]; // Array of hidden line IDs
  onWeightsChange: (
    weights: Record<
      string,
      {
        surveyId: string;
        configurationId: string;
        datasetElementQuestionId: string;
        datasetElementVariableId: string;
        weightVariableLabel: string;
      }
    >,
  ) => void;
  disabled?: boolean;
}

const PerLineWeightConfig: React.FC<PerLineWeightConfigProps> = ({
  datasetId,
  lineLabels,
  lineIds,
  customLineLabels = {},
  hiddenLines = [],
  onWeightsChange,
  disabled = false,
}) => {
  const {
    datasetId: resolvedDatasetId,
    isMultiSurvey,
    weightConfigurations,
    isLoadingWeights,
    updateLineWeight,
    clearLineWeight,
    clearAllWeights,
    isAllLinesConfigured,
    getLineWeight,
  } = usePerLineWeights({
    datasetId,
    lineLabels,
    lineIds,
    onWeightsChange,
  });

  if (isMultiSurvey) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Per-line weights for datasets with multiple surveys is not yet supported.
      </Alert>
    );
  }

  if (isLoadingWeights) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading weight configurations...
        </Typography>
      </Box>
    );
  }

  if (!weightConfigurations || weightConfigurations.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No weight configurations available for this dataset. Please configure
        weights in Dataset Configuration first.
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Accordion
        defaultExpanded
        sx={{ boxShadow: "none", border: "1px solid #eee" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Per-line weighting
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 1,
              }}
            >
              <HPButton
                size="small"
                variant="outlined"
                onClick={clearAllWeights}
                disabled={disabled || lineIds.length === 0}
              >
                Set all to Default Weight
              </HPButton>
            </Box>

            {!isAllLinesConfigured && lineIds.length > 0 && (
              <Alert severity="info" sx={{ mb: 2, fontSize: "0.875rem" }}>
                Lines without custom weights will use the dataset default
                weight.
              </Alert>
            )}

            {lineIds.length === 0 && (
              <Alert severity="info" sx={{ mb: 2, fontSize: "0.875rem" }}>
                Waiting for line chart data to load line information...
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {lineIds.map((lineId, index) => {
                // Get the display label: custom label > original label
                const originalLabel = lineLabels[index] || lineId;
                const displayLabel = customLineLabels[lineId] || originalLabel;
                const isHidden = hiddenLines.includes(lineId);

                return (
                  <React.Fragment key={lineId}>
                    <PerLineWeightRow
                      lineId={lineId}
                      lineLabel={displayLabel}
                      datasetId={resolvedDatasetId}
                      weightConfigurations={weightConfigurations}
                      selectedWeight={getLineWeight(lineId)}
                      onWeightChange={(
                        surveyId,
                        configurationId,
                        datasetElementQuestionId,
                        datasetElementVariableId,
                        variableLabel,
                      ) =>
                        updateLineWeight(
                          lineId,
                          surveyId,
                          configurationId,
                          datasetElementQuestionId,
                          datasetElementVariableId,
                          variableLabel,
                        )
                      }
                      onClearWeight={clearLineWeight}
                      isHiddenLine={isHidden}
                      disabled={disabled}
                    />
                    {index < lineIds.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PerLineWeightConfig;
